import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import * as React from 'react'

import { useTimePicker } from './hooks'
import type { TimePickerProps } from './typings'
import { filterOptions, getOptionLabel } from './utils'

/**
 * TimePicker component. Renders a dropdown of times optionally allows users to enter custom time.
 * @note uses field-level validation to allow input value formatting/validation
 *
 * @example
 * type FormModel = {
 *   time: Time
 *   time2: Time
 *   time3: Time
 * }
 *
 * const initalValues: FormModel = {
 *   time: null,
 *   time2: null,
 *   time3: null,
 * }
 *
 * // 30 minute intervals b/t 9am and 5pm
 * const timeSlots = buildTimeSlots(30, 9, 17)
 * // 30 minute intervals b/t 5am and noon
 * const morning = buildTimeSlots(30, 5, 12)
 *
 * const TimePickerDemo = () => (
 *   <Formik
 *     initialValues={initalValues}
 *     onSubmit={async (values) => console.log(values)}
 *   >
 *     {({ submitForm }) => (
 *       <>
 *         <TimePicker name="time" label="Basic" timeSlots={timeSlots} />
 *
 *         <TimePicker
 *           required
 *           name="time2"
 *           timeSlots={timeSlots}
 *           label="Validation Labels"
 *           invalidErrorText="Invalid time format"
 *           requiredErrorText="This field is required"
 *         />
 *
 *         <TimePicker
 *           required
 *           name="time3"
 *           allowUnlistedTimes
 *           timeSlots={morning}
 *           label="Custom Validation"
 *           validate={(v) => {
 *             if (v > morning[morning.length - 1]) return 'Must be before noon'
 *           }}
 *         />
 *
 *         <button onClick={submitForm}>Submit</button>
 *       </>
 *     )}
 *   </Formik>
 * )
 */

export function TimePicker(props: TimePickerProps) {
  const {
    label,
    required,
    disabled,
    fullWidth,
    timeSlots,
    testId = '',
    size = 'medium',
    allowUnlistedTimes,
  } = props

  const {
    onBlur,

    value,
    onChange,

    inputValue,
    onInputChange,

    open,
    onOpen,
    onClose,

    error,
    helperText,

    placeholder,
    tidalAdditionalStyling = false,
    tidalThemeActive,
  } = useTimePicker(props)

  return (
    <Autocomplete
      open={open}
      value={value}
      forcePopupIcon
      onOpen={onOpen}
      onBlur={onBlur}
      onClose={onClose}
      options={timeSlots}
      onChange={onChange}
      disabled={disabled}
      fullWidth={fullWidth}
      inputValue={inputValue}
      onInputChange={onInputChange}
      freeSolo={allowUnlistedTimes}
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel}
      renderInput={(params) => (
        <TextField
          {...params}
          size={size}
          label={label}
          error={error}
          required={required}
          data-test-id={testId}
          helperText={helperText}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            sx:
              tidalThemeActive && tidalAdditionalStyling
                ? {
                    paddingTop: '8.5px',
                    paddingBottom: '8.5px',
                    fontSize: '14px',
                  }
                : {},
          }}
        />
      )}
    />
  )
}
